import { useState, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Send, CheckCircle, Bot, User as UserIcon,
  Paperclip, FileText, Download, Upload,
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { useTicketDetail } from '../hooks/useTickets'
import { useProjects } from '../hooks/useProjects'
import { useToast } from '../contexts/ToastContext'
import { StatusBadge } from '../components/StatusBadge'
import { CategoryIcon } from '../components/CategoryIcon'
import { PriorityBadge } from '../components/PriorityBadge'
import { supportApi } from '../services/api'
import type { TicketAttachment } from '../services/api'

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function AttachmentRow({
  attachment,
  ticketId,
}: {
  attachment: TicketAttachment
  ticketId: string
}) {
  const [fetching, setFetching] = useState(false)

  const handleDownload = async () => {
    setFetching(true)
    try {
      const { url } = await supportApi.getAttachmentSignedUrl(ticketId, attachment.id)
      window.open(url, '_blank', 'noreferrer')
    } finally {
      setFetching(false)
    }
  }

  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors group">
      <FileText className="w-4 h-4 text-gray-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-700 truncate font-medium">{attachment.filename}</p>
        <p className="text-xs text-gray-400">{formatFileSize(attachment.size_bytes)}</p>
      </div>
      <button
        onClick={handleDownload}
        disabled={fetching}
        className="p-1.5 text-gray-400 hover:text-brand-500 opacity-0 group-hover:opacity-100 transition-all disabled:opacity-50"
        aria-label={`Download ${attachment.filename}`}
      >
        {fetching ? (
          <div className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
        ) : (
          <Download className="w-3.5 h-3.5" />
        )}
      </button>
    </div>
  )
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function TicketDetail() {
  const { projectSlug, ticketId } = useParams<{ projectSlug: string; ticketId: string }>()
  const navigate = useNavigate()
  const { projects } = useProjects()
  const currentProject = projects.find((p) => p.short_uuid === projectSlug)
  const { ticket, loading, error, addResponse, updateStatus, refresh } = useTicketDetail(ticketId)
  const { toast } = useToast()

  const [reply, setReply] = useState('')
  const [sending, setSending] = useState(false)
  const [statusUpdating, setStatusUpdating] = useState(false)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file || !ticketId) return
      // Clear the input so the same file can be re-selected after an error
      e.target.value = ''
      setUploading(true)
      try {
        await supportApi.uploadAttachment(ticketId, file)
        await refresh()
        toast('File attached successfully', 'success')
      } catch (err: unknown) {
        toast(
          err instanceof Error ? err.message : 'Upload failed — please try again',
          'error'
        )
      } finally {
        setUploading(false)
      }
    },
    [ticketId, refresh, toast]
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (error || !ticket) {
    return (
      <div className="text-center py-20">
        <p className="text-red-600 mb-4">{error || 'Ticket not found'}</p>
        <button
          onClick={() => navigate(`/${projectSlug}`)}
          className="text-sm text-brand-500 hover:text-brand-600 font-medium"
        >
          Back to project
        </button>
      </div>
    )
  }

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!reply.trim()) return
    setSending(true)
    try {
      await addResponse(reply.trim())
      setReply('')
    } catch {
      // Error is shown via the hook
    } finally {
      setSending(false)
    }
  }

  const handleResolve = async () => {
    setStatusUpdating(true)
    try {
      await updateStatus('resolved')
    } finally {
      setStatusUpdating(false)
    }
  }

  const canResolve = !['resolved', 'closed'].includes(ticket.status)

  return (
    <div className="max-w-3xl mx-auto">
      {/* Back link */}
      <button
        onClick={() => navigate(`/${projectSlug}`)}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to {currentProject?.name || 'project'}
      </button>

      {/* Ticket header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-gray-400 font-mono">{ticket.ticket_number}</span>
              <StatusBadge status={ticket.status} />
            </div>
            <h1 className="text-lg font-semibold text-gray-900">{ticket.subject}</h1>
            <div className="flex items-center gap-3 mt-2">
              <CategoryIcon category={ticket.category} showLabel />
              <PriorityBadge priority={ticket.priority} />
              <span className="text-xs text-gray-400">{formatDate(ticket.created_at)}</span>
            </div>
          </div>
          {canResolve && (
            <button
              onClick={handleResolve}
              disabled={statusUpdating}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              <CheckCircle className="w-4 h-4" />
              Mark Resolved
            </button>
          )}
        </div>
      </div>

      {/* Message thread */}
      <div className="space-y-3 mb-4">
        {/* Original description */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
              <UserIcon className="w-4 h-4 text-gray-500" />
            </div>
            <span className="text-sm font-medium text-gray-700">You</span>
            <span className="text-xs text-gray-400">{formatDate(ticket.created_at)}</span>
          </div>
          <div className="prose prose-sm max-w-none text-gray-700">
            <ReactMarkdown>{ticket.description}</ReactMarkdown>
          </div>
        </div>

        {/* Responses */}
        {ticket.responses
          ?.filter((r) => !r.is_internal)
          .map((response) => {
            const isAI = response.responder_type === 'ai'
            const isSystem = response.responder_type === 'system'
            return (
              <div
                key={response.id}
                className={`rounded-xl border p-5 ${
                  isAI
                    ? 'bg-brand-50 border-brand-200'
                    : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center ${
                      isAI ? 'bg-brand-100' : 'bg-gray-100'
                    }`}
                  >
                    {isAI ? (
                      <Bot className="w-4 h-4 text-brand-600" />
                    ) : (
                      <UserIcon className="w-4 h-4 text-gray-500" />
                    )}
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    {isAI
                      ? 'LlamAssist AI'
                      : isSystem
                        ? 'System'
                        : response.responder_name || 'Support Team'}
                  </span>
                  <span className="text-xs text-gray-400">{formatDate(response.created_at)}</span>
                </div>
                <div className="prose prose-sm max-w-none text-gray-700">
                  <ReactMarkdown>{response.content}</ReactMarkdown>
                </div>
              </div>
            )
          })}
      </div>

      {/* Attachments */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Paperclip className="w-4 h-4 text-gray-400" />
            Attachments
            {ticket.attachments && ticket.attachments.length > 0 && (
              <span className="text-xs text-gray-400 font-normal">
                ({ticket.attachments.length})
              </span>
            )}
          </h3>
          {!['closed'].includes(ticket.status) && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.log"
                onChange={handleFileSelect}
                aria-label="Attach file"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-brand-600 border border-gray-200 hover:border-brand-300 px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <div className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Upload className="w-3.5 h-3.5" />
                )}
                {uploading ? 'Uploading…' : 'Attach file'}
              </button>
            </>
          )}
        </div>
        {ticket.attachments && ticket.attachments.length > 0 ? (
          <div className="space-y-2">
            {ticket.attachments.map((a) => (
              <AttachmentRow key={a.id} attachment={a} ticketId={ticket.id} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400">No attachments yet.</p>
        )}
      </div>

      {/* Reply form */}
      {!['closed'].includes(ticket.status) && (
        <form onSubmit={handleReply} className="bg-white rounded-xl border border-gray-200 p-4">
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Write a reply..."
            rows={3}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm resize-y mb-3"
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={sending || !reply.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg font-medium hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
            >
              {sending ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Reply
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
